import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { MetadataIcon } from "@/types";
import { Integration } from "@cohesive/api-client";
import { Pencil } from "lucide-react";
import { useEffect, useState } from "react";
import { titleCase } from "title-case";
import { useBreakpoint } from "../../hooks/useBreakpoint";
import { IconWithBackground } from "../Icon";
import { IconSelector } from "../IconSelector/IconSelector";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Textarea } from "../ui/textarea";

interface TitleProps {
  resourceType?: "project" | "skill" | "assistant" | "group" | "chat";
  title: string;
  icon?: MetadataIcon | null;
  onIconUpdate?: (icon?: MetadataIcon | null) => void;
  onTitleUpdate?: (newTitle: string) => void;
  className?: string;
  titleClassName?: string;
  readOnly?: boolean;
  integrations?: Integration[];
}

export const Title = ({
  resourceType = "project",
  title,
  icon = null,
  onIconUpdate,
  onTitleUpdate,
  titleClassName,
  className,
  readOnly = false,
  integrations = [],
}: TitleProps) => {
  const md = useBreakpoint("md");
  const [currentIcon, setCurrentIcon] = useState<
    MetadataIcon | null | undefined
  >(icon);

  const [newTitle, setNewTitle] = useState(title);

  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const handleIconSelect = (selectedIcon?: MetadataIcon | null) => {
    if (!readOnly) {
      setCurrentIcon(selectedIcon);
      onIconUpdate?.(selectedIcon);
    }
  };

  useEffect(() => {
    setCurrentIcon(icon);
  }, [icon]);

  const canEdit = !readOnly && typeof onTitleUpdate === "function";

  const TitleElement = (
    <div
      className={cn("w-full flex items-center gap-2 group", {
        "cursor-pointer": canEdit,
      })}
    >
      <h1
        className={cn("text-center", {
          "text-left": md,
          "text-[24px]": title?.length > 50,
          "text-[20px]": title?.length > 80,
          "text-[18px]": title?.length > 100,
        })}
      >
        {title}
      </h1>
      {canEdit && md && (
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button
              variant="ghost"
              className="rounded-full p-0 w-8 h-8 opacity-0 scale-75 group-hover:opacity-100 group-hover:scale-100 transition-all duration-200"
              aria-label="Edit title"
            >
              <Pencil className="w-4 h-4" />
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit {titleCase(resourceType)} Title</DialogTitle>
              <DialogDescription>
                Enter a new name for your {resourceType}.
              </DialogDescription>
            </DialogHeader>
            <Input
              defaultValue={title}
              onChange={(e) => setNewTitle(e.target.value)}
              className="w-full"
              autoFocus
            />
            <DialogFooter>
              <Button
                variant="secondary"
                onClick={() => setIsDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  onTitleUpdate?.(newTitle);
                  setIsDialogOpen(false);
                }}
              >
                Save
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );

  return (
    <div
      className={cn("relative flex flex-col items-center w-full", className)}
    >
      <div
        className={cn(
          "flex items-center flex-col md:flex-row relative text-2xl mb-6 text-center",
          titleClassName,
        )}
      >
        {currentIcon &&
          (readOnly ? (
            currentIcon && (
              <div className="text-2xl md:mr-1 p-2 rounded text-[50px]">
                <IconWithBackground icon={currentIcon} size={48} />
              </div>
            )
          ) : (
            <Popover>
              <PopoverTrigger asChild>
                <div
                  role="button"
                  aria-label="Update icon"
                  className="text-2xl md:mr-2 cursor-pointer p-2 rounded duration-300 ease-in-out transition-all relative group"
                >
                  <IconWithBackground icon={currentIcon} size={48} />
                  <div className="absolute bottom-1 right-1 opacity-0 scale-75 group-hover:opacity-100 group-hover:scale-100 transition-all duration-200">
                    <div className="bg-background border rounded-full p-1 shadow-2xs">
                      <Pencil className="w-3 h-3" />
                    </div>
                  </div>
                </div>
              </PopoverTrigger>
              <PopoverContent className="p-0 w-[352px]">
                <IconSelector
                  onIconChange={handleIconSelect}
                  selectedIcon={currentIcon}
                  integrations={integrations}
                />
              </PopoverContent>
            </Popover>
          ))}

        {TitleElement}
      </div>
    </div>
  );
};

interface DescriptionProps {
  resourceType: "project" | "skill" | "assistant" | "group" | "chat";
  description?: string;
  onDescriptionUpdate?: (newDescription: string) => void;
  readOnly?: boolean;
}

export const Description = ({
  resourceType = "project",
  description,
  onDescriptionUpdate,
  readOnly = false,
}: DescriptionProps) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newDescription, setNewDescription] = useState(description || "");

  const canEdit = !readOnly && typeof onDescriptionUpdate === "function";

  const DescriptionElement = (
    <div
      className={cn(
        "relative flex flex-col items-center justify-center text-center gap-2 group mb-10",
        {
          "cursor-pointer": canEdit,
        },
      )}
    >
      {readOnly && description ? (
        <p className="text-sm font-normal text-center text-muted-foreground">
          {description}
        </p>
      ) : !readOnly ? (
        <p className="text-sm font-normal text-center text-muted-foreground group-hover:opacity-70 transition-opacity duration-200">
          {description || "Add a description"}
        </p>
      ) : null}

      {canEdit && (
        <Button
          variant="ghost"
          className="absolute -top-8 rounded-full p-0 w-8 h-8 opacity-0 scale-75 group-hover:opacity-100 group-hover:scale-100 transition-all duration-200"
          aria-label="Edit description"
        >
          <Pencil className="w-4 h-4" />
        </Button>
      )}
    </div>
  );

  if (readOnly || typeof onDescriptionUpdate !== "function") {
    return DescriptionElement;
  }

  return (
    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <DialogTrigger asChild>{DescriptionElement}</DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit {titleCase(resourceType)} Description</DialogTitle>
          <DialogDescription>
            Enter a new description for your {resourceType}.
          </DialogDescription>
        </DialogHeader>
        <Textarea
          defaultValue={description}
          onChange={(e) => setNewDescription(e.target.value)}
          className="w-full min-h-[100px]"
          placeholder={`Add a description for your ${resourceType}...`}
          autoFocus
        />
        <DialogFooter>
          <Button variant="secondary" onClick={() => setIsDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              onDescriptionUpdate?.(newDescription);
              setIsDialogOpen(false);
            }}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
